import { Link } from '@tanstack/react-router';
import { KeyRound, Plus, ShieldCheck } from 'lucide-react';
import { useState } from 'react';

import DeleteTeamModal from '@/components/DeleteTeamModal';
import RemoveUsageCreditsModal from '@/components/RemoveUsageCreditsModal';
import TeamCreditsModal from '@/components/TeamCreditsModal';
import TeamDiscountModal from '@/components/TeamDiscountModal';
import TeamUsageCreditsModal from '@/components/TeamUsageCreditsModal';
import { useToast } from '@/components/ToastProvider';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { statusVariant } from '@/components/UserEditTeamSection';
import {
  useCancelTeamSubscription,
  useDisableTeamBaa,
  useDisableTeamSso,
  useEnableTeamBaa,
  useEnableTeamSso,
  useResumeTeamSubscription,
  useTransferTeamOwnership,
} from '@/hooks/useTeams';
import {
  formatCents,
  formatUnixDate,
  isHealthySubscriptionStatus,
} from '@/lib/utils';

import { ActionMenu, ActionMenuItem } from './ActionMenu';
import { AddMembersDialog } from './AddMembersDialog';
import { ExtendTrialDialog } from './ExtendTrialDialog';
import { relativeDays, shortDate, TIER_OPTIONS } from './format';
import {
  useChangeTeamMemberTier,
  useExtendTeamTrial,
  useRemoveTeamMember,
  useResendTeamInvite,
  useTeam,
} from './useAccounts';

type OpenDialog =
  | 'extend'
  | 'members'
  | 'credits'
  | 'usage'
  | 'discount'
  | 'delete'
  | 'remove-usage'
  | null;

const tierLabel = (tier: string) => tier[0].toUpperCase() + tier.slice(1);

export function TeamPage({ teamId }: { teamId: string }) {
  const { data: team, isLoading, error } = useTeam(teamId);
  const [openDialog, setOpenDialog] = useState<OpenDialog>(null);
  const toast = useToast();
  const extendTrial = useExtendTeamTrial(teamId);
  const resendInvite = useResendTeamInvite(teamId);
  const changeTier = useChangeTeamMemberTier(teamId);
  const removeMember = useRemoveTeamMember(teamId);
  const transferOwnership = useTransferTeamOwnership();
  const cancelSub = useCancelTeamSubscription();
  const resumeSub = useResumeTeamSubscription();
  const enableBaa = useEnableTeamBaa();
  const disableBaa = useDisableTeamBaa();
  const enableSso = useEnableTeamSso();
  const disableSso = useDisableTeamSso();

  if (isLoading) return <p className="py-8 text-center">Loading team...</p>;
  if (!team || error) {
    return (
      <p className="py-8 text-center text-sm">
        Could not open this team. It may have been deleted, or it has no
        members.{' '}
        <Link to="/teams" className="underline">
          Back to teams
        </Link>
      </p>
    );
  }

  const status = statusVariant(team.subscription_status);
  const trialEndMs =
    team.subscription_status === 'trialing' && team.trial_end
      ? team.trial_end * 1000
      : null;
  const activeCount = team.members.filter((m) => m.status === 'active').length;

  const run = async (action: () => Promise<unknown>, done: string) => {
    try {
      await action();
      toast.success(done);
    } catch (e) {
      toast.error(
        'That did not work',
        e instanceof Error ? e.message : 'Unknown error'
      );
    }
  };
  const confirmThen = (
    question: string,
    action: () => Promise<unknown>,
    done: string
  ) => {
    if (window.confirm(question)) void run(action, done);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="space-y-1">
          <Link
            to="/teams"
            className="text-sm text-muted-foreground hover:text-foreground"
          >
            ← Teams
          </Link>
          <h1 className="text-2xl font-semibold">{team.name}</h1>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Badge variant="outline" className={status.className}>
              {status.label}
            </Badge>
            {trialEndMs && <span>Trial ends {shortDate(trialEndMs)}</span>}
            {team.cancel_at_period_end && (
              <span>Cancels {formatUnixDate(team.current_period_end)}</span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={() => setOpenDialog('extend')}
            disabled={!trialEndMs}
            title={
              trialEndMs ? undefined : 'Only a team on a trial can be extended'
            }
          >
            Extend trial
          </Button>
          <ActionMenu>
            {team.cancel_at_period_end ? (
              <ActionMenuItem
                onClick={() =>
                  run(
                    () => resumeSub.mutateAsync({ teamId }),
                    'Subscription resumed'
                  )
                }
              >
                Resume subscription
              </ActionMenuItem>
            ) : (
              <ActionMenuItem
                destructive
                disabledReason={
                  isHealthySubscriptionStatus(team.subscription_status)
                    ? undefined
                    : 'No active subscription'
                }
                onClick={() =>
                  confirmThen(
                    `Cancel ${team.name}'s subscription? All ${activeCount} members move to the free plan on ${formatUnixDate(team.current_period_end)}.`,
                    () => cancelSub.mutateAsync({ teamId }),
                    'Subscription cancelled'
                  )
                }
              >
                Cancel subscription
              </ActionMenuItem>
            )}
            <ActionMenuItem destructive onClick={() => setOpenDialog('delete')}>
              Delete team
            </ActionMenuItem>
          </ActionMenu>
        </div>
      </div>

      <Card>
        <CardHeader className="flex-row items-center justify-between space-y-0">
          <CardTitle>
            Members ({activeCount} of {team.seat_count} seats)
          </CardTitle>
          <Button size="sm" onClick={() => setOpenDialog('members')}>
            <Plus className="mr-1 h-4 w-4" />
            Add members
          </Button>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Email</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Tier</TableHead>
                <TableHead>Status</TableHead>
                <TableHead />
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {team.members.map((m) => {
                const isInvite = m.status === 'invited';
                const since = isInvite ? m.invited_at : m.joined_at;
                return (
                  <TableRow key={m.membership_id}>
                    <TableCell>{m.email}</TableCell>
                    <TableCell className="capitalize">{m.role}</TableCell>
                    <TableCell className="capitalize">{m.tier}</TableCell>
                    <TableCell>
                      <Badge
                        variant="outline"
                        className={
                          statusVariant(isInvite ? 'trialing' : 'active')
                            .className
                        }
                      >
                        {m.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {since &&
                        `${isInvite ? 'Invited' : 'Joined'} ${relativeDays(Date.parse(since))}`}
                    </TableCell>
                    <TableCell className="text-right">
                      <ActionMenu>
                        {isInvite && (
                          <ActionMenuItem
                            onClick={() =>
                              run(
                                () => resendInvite.mutateAsync(m.membership_id),
                                `Invite resent to ${m.email}`
                              )
                            }
                          >
                            Resend invite
                          </ActionMenuItem>
                        )}
                        {TIER_OPTIONS.filter((tier) => tier !== m.tier).map(
                          (tier) => (
                            <ActionMenuItem
                              key={tier}
                              onClick={() =>
                                confirmThen(
                                  `Move ${m.email} to ${tierLabel(tier)}? Their seat on the team's subscription moves to ${tierLabel(tier)}.`,
                                  () =>
                                    changeTier.mutateAsync({
                                      membershipId: m.membership_id,
                                      tier,
                                    }),
                                  `${m.email} is now on ${tierLabel(tier)}`
                                )
                              }
                            >
                              Change tier to {tierLabel(tier)}
                            </ActionMenuItem>
                          )
                        )}
                        {m.status === 'active' && m.role !== 'owner' && (
                          <ActionMenuItem
                            onClick={() =>
                              confirmThen(
                                `Make ${m.email} the owner of ${team.name}? The current owner becomes an admin.`,
                                () =>
                                  transferOwnership.mutateAsync({
                                    teamId,
                                    membershipId: m.membership_id,
                                  }),
                                `${m.email} is now the owner`
                              )
                            }
                          >
                            Make owner
                          </ActionMenuItem>
                        )}
                        <ActionMenuItem
                          destructive
                          onClick={() =>
                            confirmThen(
                              isInvite
                                ? `Revoke the invite for ${m.email}? The team pays for one seat fewer.`
                                : `Remove ${m.email} from ${team.name}? They move to the free plan and the team pays for one seat fewer.`,
                              () => removeMember.mutateAsync(m.membership_id),
                              isInvite ? 'Invite revoked' : `${m.email} removed`
                            )
                          }
                        >
                          {isInvite ? 'Revoke invite' : 'Remove from team'}
                        </ActionMenuItem>
                      </ActionMenu>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Billing</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 gap-4 text-sm sm:grid-cols-3">
            <div className="rounded-md border p-3">
              <p className="text-xs text-muted-foreground">
                {team.cancel_at_period_end ? 'Cancels' : 'Renews'}
              </p>
              <p className="mt-1 font-semibold">
                {formatUnixDate(team.current_period_end)}
              </p>
              <p className="mt-1 text-xs capitalize text-muted-foreground">
                {team.billing_interval}
              </p>
            </div>
            <div className="rounded-md border p-3">
              <p className="text-xs text-muted-foreground">
                Team usage credits
              </p>
              <p className="mt-1 font-semibold">
                {team.team_wallet_purchased_balance.toLocaleString()}
              </p>
            </div>
            <div className="rounded-md border p-3">
              <p className="text-xs text-muted-foreground">
                Stripe invoice credit
              </p>
              <p className="mt-1 font-semibold">
                {formatCents(team.account_credit_cents)}
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={!team.stripe_customer_id}
              onClick={() => setOpenDialog('credits')}
            >
              Add Stripe invoice credit
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setOpenDialog('usage')}
            >
              Add usage credits
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setOpenDialog('remove-usage')}
            >
              Remove usage credits
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={!isHealthySubscriptionStatus(team.subscription_status)}
              onClick={() => setOpenDialog('discount')}
            >
              Apply team discount
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Security</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-2">
          <label className="inline-flex items-center gap-2 rounded-md border px-3 py-1.5 text-sm">
            <ShieldCheck className="h-4 w-4 text-muted-foreground" />
            HIPAA BAA
            <Switch
              checked={team.baa_enabled}
              disabled={enableBaa.isPending || disableBaa.isPending}
              onCheckedChange={(next) => {
                if (!next) {
                  confirmThen(
                    `Turn off the BAA for ${team.name}? Members who did not sign their own BAA lose HIPAA privacy mode.`,
                    () => disableBaa.mutateAsync({ teamId }),
                    'Team BAA turned off'
                  );
                  return;
                }
                const legalName = window.prompt(
                  `Legal name of the person who signed the BAA for ${team.name}`
                );
                if (legalName?.trim()) {
                  void run(
                    () =>
                      enableBaa.mutateAsync({
                        teamId,
                        legalName: legalName.trim(),
                      }),
                    'Team BAA turned on'
                  );
                }
              }}
            />
          </label>
          <label className="inline-flex items-center gap-2 rounded-md border px-3 py-1.5 text-sm">
            <KeyRound className="h-4 w-4 text-muted-foreground" />
            SSO
            <Switch
              checked={team.sso_enabled}
              disabled={enableSso.isPending || disableSso.isPending}
              onCheckedChange={(next) =>
                confirmThen(
                  next
                    ? `Turn on SSO for ${team.name}? Team admins can then connect their identity provider.`
                    : `Turn off SSO for ${team.name}? Members can sign in any way again.`,
                  () => (next ? enableSso : disableSso).mutateAsync({ teamId }),
                  next ? 'Team SSO turned on' : 'Team SSO turned off'
                )
              }
            />
          </label>
        </CardContent>
      </Card>

      {openDialog === 'extend' && trialEndMs && (
        <ExtendTrialDialog
          onClose={() => setOpenDialog(null)}
          name={team.name}
          currentEndMs={trialEndMs}
          onExtend={(trialEnd) => extendTrial.mutateAsync(trialEnd)}
        />
      )}
      {openDialog === 'members' && (
        <AddMembersDialog
          teamId={teamId}
          teamName={team.name}
          onClose={() => setOpenDialog(null)}
        />
      )}
      <TeamCreditsModal
        isOpen={openDialog === 'credits'}
        onClose={() => setOpenDialog(null)}
        teamId={team.id}
        teamName={team.name}
      />
      <TeamUsageCreditsModal
        isOpen={openDialog === 'usage'}
        onClose={() => setOpenDialog(null)}
        teamId={team.id}
        rateCentsPer1000={team.rate_cents_per_1000}
      />
      <RemoveUsageCreditsModal
        isOpen={openDialog === 'remove-usage'}
        onClose={() => setOpenDialog(null)}
        wallet={{ kind: 'team', teamId: team.id }}
        purchasedBalance={team.team_wallet_purchased_balance}
        rateCentsPer1000={team.rate_cents_per_1000}
      />
      <TeamDiscountModal
        isOpen={openDialog === 'discount'}
        onClose={() => setOpenDialog(null)}
        teamId={team.id}
        teamName={team.name}
      />
      <DeleteTeamModal
        isOpen={openDialog === 'delete'}
        onClose={() => setOpenDialog(null)}
        team={team}
      />
    </div>
  );
}
